import { useMemo, useState } from "react";
import { message } from "antd";
import { t } from "i18next";
import { showFallbackErrorToast } from "@apis/http/httpErrorToast";
import { IconLineTrash } from "@kalodata/kds-design-icons";
import { useSerializeCallback } from "@kalodata/tools";
import Image from "@components/Image";
import { ClipMoreMenu } from "@components/ClipMoreMenu/ClipMoreMenu";
import { CreateProductEntityModal } from "@pages/library/CreateProductEntityModal";
import { UIProductSubImages } from "./UIProductSubImages";
import { deleteUserProduct, type UserProductRecord } from "@apis/http/api";

interface UICreatedProductCardProps {
  record: UserProductRecord;
  onRefresh?: () => void;
}

export const UICreatedProductCard = ({ record, onRefresh }: UICreatedProductCardProps) => {
  const [editOpen, setEditOpen] = useState(false);

  const handleCardClick = () => {
    setEditOpen(true);
  };

  const handleDelete = useSerializeCallback(async () => {
    try {
      await deleteUserProduct({ productId: record.id } as any);
      message.success(t("Product deleted"));
      onRefresh?.();
    } catch (err: any) {
      showFallbackErrorToast(err, t("Failed to delete product"));
    }
  });

  const moreMenuOptions = useMemo(
    () => [
      {
        key: "delete",
        label: "Delete",
        icon: <IconLineTrash size={20} />,
        loading: handleDelete.isPending(),
        onClick: handleDelete,
      },
    ],
    [handleDelete],
  );

  return (
    <>
      <ClipMoreMenu
        placement="bottomRight"
        className="bg-kds-bg border border-kds-border rounded-[8px] overflow-hidden flex flex-col w-full cursor-pointer"
        options={moreMenuOptions}
      >
        <div key={record.id} className="flex flex-col w-full" onClick={handleCardClick}>
          <div className="relative aspect-square w-full shrink-0">
            <Image className="w-full h-full rounded-none! border-none!" url={record.mainImage?.url} />
            <UIProductSubImages images={record.images ?? []} className="hidden group-hover/clipMore:flex" />
          </div>
          <div className="shrink-0 p-[16px] min-w-0">
            <p className="m-0 h-[42px] overflow-hidden text-kds-fg text-[14px] leading-[21px] line-clamp-2">
              {(record.title ?? "").replace(/[\r\n]+/g, " ")}
            </p>
          </div>
        </div>
      </ClipMoreMenu>

      <CreateProductEntityModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        productId={record.id}
        onSuccess={onRefresh}
      />
    </>
  );
};
